import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
	Button,
	Card,
	Empty,
	Flex,
	Modal,
	Popconfirm,
	Select,
	Tag,
	Typography,
	notification,
} from "antd";
import { DeleteFilled, EditFilled, PlusCircleFilled } from "@ant-design/icons";
import { api } from "../utils/Api";
import { CustomSpinner } from "../utils/CustomComponents";
import { filterOption } from "./shiftGroupUtils";
import DriverDefaultGroupFormModal from "./DriverDefaultGroupFormModal";
const { Text } = Typography;

const formatDate = (value) => dayjs(value).format("MM/DD/YYYY hh:mm A");

// Default groups of drivers of each plant.
// Each time it opens it takes the plant of the dashboard; changing it here only affects this modal.
const DriverDefaultGroupsModal = ({
	open,
	onClose,
	plantOptions,
	loadingPlants,
	dashboardPlant,
}) => {
	const [plant, setPlant] = useState(null);
	const [groups, setGroups] = useState([]);
	const [loadingGroups, setLoadingGroups] = useState(false);
	// { group } — group null when creating a new one
	const [formModal, setFormModal] = useState(null);
	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	// Cleared on close so reopening never loads the groups of the previous plant
	useEffect(() => {
		setPlant(open ? dashboardPlant ?? null : null);
	}, [open]);

	useEffect(() => {
		if (open && plant) {
			getGroups();
		} else {
			setGroups([]);
		}
	}, [open, plant]);

	const getGroups = async () => {
		const url = api + "/shift-group-member/list";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingGroups(true);
			const { data } = await axios.get(url, { params: { plant: plant } });
			if (data.success) {
				setGroups(data.payload);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get groups");
		} finally {
			setLoadingGroups(false);
		}
	};

	const deleteGroup = async (group) => {
		const url = api + `/shift-group-member/delete/${group._id}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.delete(url);
			if (data.success) {
				getGroups();
				openNotification("success", "Success", data.message);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to delete group");
		}
	};

	const RenderGroups = () => {
		if (!plant) {
			return <Empty description="Select a plant to see its groups" />;
		}

		if (loadingGroups) {
			return <CustomSpinner />;
		}

		if (groups.length === 0) {
			return <Empty description="No groups created" />;
		}

		return (
			<Flex vertical gap="middle">
				{groups.map((group) => (
					<Card
						key={group._id}
						size="small"
						title={group.name}
						extra={
							<Flex gap="small">
								<Button
									type="primary"
									icon={<EditFilled />}
									title="Edit group"
									onClick={() => setFormModal({ group })}
								/>
								<Popconfirm
									title="Delete group"
									description="Are you sure you want to delete this group?"
									okText="Delete"
									cancelText="Cancel"
									okButtonProps={{ danger: true }}
									onConfirm={() => deleteGroup(group)}
								>
									<Button
										danger
										type="primary"
										icon={<DeleteFilled />}
										title="Delete group"
									/>
								</Popconfirm>
							</Flex>
						}
					>
						<Flex vertical gap="small">
							<Text>
								<Text strong>Plant:</Text> {group.plant?.name ?? ""}
							</Text>
							<Text>
								<Text strong>Drivers ({group.members.length}):</Text>
							</Text>
							<Flex wrap="wrap" gap="small">
								{group.members.map((member) => (
									<Tag key={member._id}>
										{member.name}
										{member.phone ? ` · ${member.phone}` : ""}
									</Tag>
								))}
							</Flex>
							<Text>
								<Text strong>Created By:</Text> {group.createdBy?.name ?? ""}
							</Text>
							<Text>
								<Text strong>Created At:</Text> {formatDate(group.createdAt)}
							</Text>
							<Text>
								<Text strong>Updated At:</Text> {formatDate(group.updatedAt)}
							</Text>
						</Flex>
					</Card>
				))}
			</Flex>
		);
	};

	return (
		<Modal
			title="Default Groups"
			open={open}
			onCancel={onClose}
			centered
			width={800}
			footer={null}
		>
			{contextHolder}
			<Flex vertical gap="large" style={{ marginTop: "20px" }}>
				<Flex justify="space-between" align="center" wrap="wrap" gap="middle">
					<Select
						showSearch
						placeholder="Select plant"
						size="large"
						options={plantOptions}
						filterOption={filterOption}
						value={plant}
						onChange={(value) => setPlant(value)}
						loading={loadingPlants}
						disabled={loadingPlants}
						style={{ width: 250 }}
					/>
					<Button
						type="primary"
						size="large"
						icon={<PlusCircleFilled />}
						disabled={!plant}
						onClick={() => setFormModal({ group: null })}
					>
						Create New Group
					</Button>
				</Flex>
				<RenderGroups />
			</Flex>
			<DriverDefaultGroupFormModal
				open={!!formModal}
				plant={plant}
				group={formModal?.group}
				usedDrivers={groups
					.filter((item) => item._id !== formModal?.group?._id)
					.flatMap((item) => item.members.map((member) => member._id))}
				onClose={() => setFormModal(null)}
				onSuccess={(message) => {
					setFormModal(null);
					getGroups();
					openNotification("success", "Success", message);
				}}
			/>
		</Modal>
	);
};

export default DriverDefaultGroupsModal;
